import { createContext, memo, useContext, useEffect, useRef, useState } from 'react'
import { BrainIcon, ChevronDownIcon } from 'lucide-react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import { Shimmer } from './shimmer'

/** 移植自 AI Elements reasoning.tsx:保留流式自动展开/结束自动收起行为;
 *  内容用纯文本渲染(原实现的 Streamdown 依赖过重,思考文本无需 markdown)。
 */
const AUTO_CLOSE_DELAY = 1000

export const Reasoning = memo(
  ({
    className,
    isStreaming = false,
    children,
  }: {
    className?: string
    isStreaming?: boolean
    children: React.ReactNode
  }) => {
    const [isOpen, setIsOpen] = useState(isStreaming)
    const hasEverStreamedRef = useRef(isStreaming)
    const [hasAutoClosed, setHasAutoClosed] = useState(false)

    useEffect(() => {
      if (isStreaming) {
        hasEverStreamedRef.current = true
        setIsOpen(true)
      }
    }, [isStreaming])

    useEffect(() => {
      if (hasEverStreamedRef.current && !isStreaming && isOpen && !hasAutoClosed) {
        const timer = setTimeout(() => {
          setIsOpen(false)
          setHasAutoClosed(true)
        }, AUTO_CLOSE_DELAY)
        return () => clearTimeout(timer)
      }
    }, [isStreaming, isOpen, hasAutoClosed])

    return (
      <Collapsible
        className={cn('not-prose my-2', className)}
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <ReasoningContext.Provider value={{ isStreaming }}>
          {children}
        </ReasoningContext.Provider>
      </Collapsible>
    )
  },
)
Reasoning.displayName = 'Reasoning'

const ReasoningContext = createContext<{ isStreaming: boolean }>({ isStreaming: false })

export const ReasoningTrigger = memo(({ className }: { className?: string }) => {
  const { isStreaming } = useContext(ReasoningContext)
  return (
    <CollapsibleTrigger
      className={cn(
        // doc 122:group 修复 — 箭头 group-data-[state=open]:rotate-180 依赖本类的 group 标记
        'group flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground',
        className,
      )}
    >
      <BrainIcon className="size-4" />
      {isStreaming ? <Shimmer duration={1}>思考中…</Shimmer> : <span>思考过程</span>}
      <ChevronDownIcon className="size-4 transition-transform group-data-[state=open]:rotate-180" />
    </CollapsibleTrigger>
  )
})
ReasoningTrigger.displayName = 'ReasoningTrigger'

export const ReasoningContent = memo(
  ({ className, children }: { className?: string; children: string }) => (
    // doc 122:高度动画重构 — 外层只做 height 过渡(collapsible-down/up keyframes,
    // index.css),padding/margin/滚动全部下沉内层 div;关闭从「淡出+unmount 跳变」
    // 改为真高度收起,开合对称,下方内容平滑上移
    <CollapsibleContent
      className={cn(
        'overflow-hidden',
        'data-[state=open]:animate-[collapsible-down_200ms_ease-out]',
        'data-[state=closed]:animate-[collapsible-up_200ms_ease-out]',
      )}
    >
      <div
        className={cn(
          'mt-2 max-h-60 overflow-y-auto rounded-md bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground',
          'whitespace-pre-wrap break-all outline-none',
          className,
        )}
      >
        {children}
      </div>
    </CollapsibleContent>
  ),
)
ReasoningContent.displayName = 'ReasoningContent'
